import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  MarketplaceItem,
  TransactionSuccessService,
  TransactionSuccessQuery,
  ProcessProviderId,
  PurchaseQuery,
  MemberPurchase,
  MemberQuery,
  MarketplaceExtendedQuery,
  AttributeValue,
  PurchaseCustomField,
  MarketplaceService,
  BasketService,
  PurchaseService,
  CartService,
} from '@fgb/core';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'fgb-marketplace-purchase-success-page',
  templateUrl: './marketplace-purchase-success-page.component.html',
  styleUrls: ['./marketplace-purchase-success-page.component.scss'],
})
export class MarketplacePurchaseSuccessPageComponent implements OnInit, OnDestroy {
  purchasedItemList: any[];
  price: number;
  quantity: number;
  theme = '';
  marketplaceItemType = '';
  attributes: AttributeValue[];
  customFields: PurchaseCustomField[];
  purchasedItemLogId: number;
  ProcessProvider = ProcessProviderId;
  private _destroyed$ = new Subject<void>();
  defaultItemName: string = '';
  defaultItemImageUrl: string = '';

  constructor(
    private transactionSuccessQuery: TransactionSuccessQuery,
    private transactionSuccessService: TransactionSuccessService,
    private purchaseQuery: PurchaseQuery,
    private memberQuery: MemberQuery,
    private marketplaceExtendedQuery: MarketplaceExtendedQuery,
    private marketplaceService: MarketplaceService,
    private activatedRoute: ActivatedRoute,
    private basketService: BasketService,
    private purchaseService: PurchaseService,
    private cartService: CartService
  ) {}

  ngOnInit() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const purchaseOrderId = queryParams?.purchaseOrderId;
    if (purchaseOrderId) {
      this.cartService.removeAllCartItems();
      this.getDetailPurchasesOrderBasket(purchaseOrderId);
    } else {
      const storeValue = this.transactionSuccessQuery.getValue();
      this.purchasedItemList = [storeValue.item as MarketplaceItem];
      this.quantity = storeValue.quantity;
      this.price = storeValue.totalPrice;
      this.theme = this.purchasedItemList[0].theme;
      this.marketplaceItemType = this._getMarketplaceItemType(this.purchasedItemList[0].ProcessProviderId);

      combineLatest([this.purchaseQuery.selectPurchases(), this.memberQuery.userContext$])
        .pipe(takeUntil(this._destroyed$))
        .subscribe(([purchases, userContext]) => {
          let orderedPurchases = purchases.filter((p) => p.OrderId === storeValue.orderId);

          let uniquePurchases: MemberPurchase[] = [];
          orderedPurchases.forEach((element) => {
            if (!uniquePurchases.some((p) => p.RewardItemId === element.RewardItemId)) {
              uniquePurchases.push(element);
              this.purchasedItemLogId = element.LogId;
            }
          });

          if (uniquePurchases && uniquePurchases[0].AttributeValues && userContext && userContext.lcid) {
            this.purchasedItemList = [storeValue.item]
              .filter((item) => item != null)
              .map((item) =>
                this.marketplaceExtendedQuery.setAttributeDisplayNameByLocale(item as MarketplaceItem, +userContext.lcid)
              );
            this.attributes = this.marketplaceExtendedQuery.getLocalisedAttributeValuesFromArray(
              uniquePurchases[0].AttributeValues,
              +userContext.lcid
            );
          }
          if (uniquePurchases && uniquePurchases[0]) {
            this.customFields = uniquePurchases[0].CustomFields;
          }
        });
    }
  }

  /**
   * This will download the purchased marketplace item.
   * @param logId is the log id of the purchased marketplace item.
   */
  downloadPurchasedItem(logId: number) {
    firstValueFrom(this.marketplaceService.downloadMarketplaceItem(logId)).then((response) => {
      const newBlob = new Blob([response], { type: response.type });
      const anchor = window.document.createElement('a');
      anchor.href = window.URL.createObjectURL(newBlob);
      // TODO: Return marketplace item download name
      anchor.download = logId.toString();
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(anchor.href);
      document.body.removeChild(anchor);
    });
  }

  async getDetailPurchasesOrderBasket(purchaseOrderId: string) {
    try {
      const res: any = await this.basketService.getPurchasesOrderBasket(purchaseOrderId);
      this.marketplaceItemType = 'MerchantValue';
      const basketItems = this.filterOutCardToken(res?.BasketItems || []);
      this.purchasedItemList = basketItems;
      const firstBasketItem = basketItems[0];

      const firstBasketItemLogId = firstBasketItem.Items[0].LogId;
      if (firstBasketItemLogId) {
        const firstItemData: any = await this.purchaseService.getDetailPurchase(firstBasketItemLogId);
        this.defaultItemName = firstItemData.DisplayName;
        this.defaultItemImageUrl = firstItemData.ImageURL;
      }
    } catch (err: any) {
      console.log('err', err);
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.transactionSuccessService.clear();
  }

  /** Gets the marketplace item type text for a given marketplace item process provider ID. */
  private _getMarketplaceItemType(processProviderId: ProcessProviderId): string {
    switch (processProviderId) {
      case ProcessProviderId.Event:
        return 'Event';
      case ProcessProviderId.VirtualEvent:
        return 'Event';
      case ProcessProviderId.Product:
        return 'Product';
      default:
        return 'Marketplace';
    }
  }

  private filterOutCardToken(items: any[]): any[] {
    return items.filter((item) => item.ItemTypeId !== 24);
  }
}
